/**
 * Hero Content Switch
 * 
 * Switches between v1 (legacy) and v2 (new design) based on GrowthBook flag.
 * 
 * After experiment concludes:
 * 1. Update ../index.tsx to export winner directly
 * 2. Delete this variants/ folder
 */

import { ExperimentalSection } from 'ui/experimental-section';
import { AllowedExperimentKey } from '@tenants/types';
import { useExperimentVariation } from 'ui/experimental-section/hooks';
import { HeroContentV1, type HeroContentV1Props } from './hero-content.v1';
import { HeroContentV2Desktop } from './hero-content.v2';
import type { HeroContentProps } from '../types';

// ============================================================================
// Types
// ============================================================================

type V2Props = Omit<HeroContentProps, 'variant'>;

export interface HeroContentSwitchProps extends V2Props, HeroContentV1Props {}

// ============================================================================
// Desktop Content Switch (for mainContent slot)
// ============================================================================

export function HeroContentSwitch({
  // v2 props
  listing,
  currency,
  locale,
  blur,
  mlsCourtesyText,
  mlsBuyerRepText,
  virtualTourLabel,
  onVirtualTourClick,
  priceHistoryLabel,
  onPriceHistoryClick,
  onAddressClick,
  listingSource,
  // v1 props
  tenantId,
  complianceRequirement,
  shouldScramble,
  priceHistoryRef,
}: HeroContentSwitchProps) {
  // Show favourite toggle ONLY when BOTH experiments are in control (neither is active)
  // Hide when EITHER experiment is in variation (to avoid duplication with sticky nav actions)
  const { showVariation: isListingHeroVariation } = useExperimentVariation(AllowedExperimentKey.LISTING_HERO);
  const { showVariation: isStickyHeaderTabsVariation } = useExperimentVariation(AllowedExperimentKey.STICKY_HEADER_TABS);
  const showFavouriteToggle = !isListingHeroVariation && !isStickyHeaderTabsVariation;

  return (
    <ExperimentalSection
      experimentKey={AllowedExperimentKey.LISTING_HERO}
      control={
        <HeroContentV1
          tenantId={tenantId}
          listing={listing}
          complianceRequirement={complianceRequirement}
          shouldScramble={shouldScramble}
          priceHistoryLabel={priceHistoryLabel}
          onPriceHistoryClick={onPriceHistoryClick}
          priceHistoryRef={priceHistoryRef}
          showFavouriteToggle={showFavouriteToggle}
          listingSource={listingSource}
        />
      }
      variation={
        <HeroContentV2Desktop
          listing={listing}
          currency={currency}
          locale={locale}
          blur={blur}
          mlsCourtesyText={mlsCourtesyText}
          mlsBuyerRepText={mlsBuyerRepText}
          virtualTourLabel={virtualTourLabel}
          onVirtualTourClick={onVirtualTourClick}
          priceHistoryLabel={priceHistoryLabel}
          onPriceHistoryClick={onPriceHistoryClick}
          onAddressClick={onAddressClick}
          listingSource={listingSource}
        />
      }
    />
  );
}
